'use client';

import { JsonLd } from '@/seo/JsonLd';
import { SITE_DOMAIN, toAbsoluteUrl } from '@/utils/url';
import { products } from '@/data/products';

type Props = {
  noIndex?: boolean;
};

export function HomeSEO({ noIndex }: Props) {
  const url = toAbsoluteUrl('/');
  const title = 'Bariyerli Bondex Sünger | Ses Yalıtım Malzemeleri - Echopan®';
  const description = 'Bariyerli Bondex Sünger ile üstün ses yalıtımı performansı. Bariyer katmanlı kompozit yapıda hava kaynaklı gürültüyü minimize edin. Türkiye geneli hızlı teslimat.';

  // Get featured products for schema
  const bondexProduct = products.find((p) => p.slug === 'bondex-sunger');
  const bariyerliProduct = products.find((p) => p.slug === 'bariyerli-bondex-sunger');
  const ciftBariyerliProduct = products.find((p) => p.slug === 'cift-bariyerli-bondex-sunger');
  const validFromStr = new Date().toISOString().split('T')[0];

  // Calculate priceValidUntil (1 year from now)
  const priceValidUntil = new Date();
  priceValidUntil.setFullYear(priceValidUntil.getFullYear() + 1);
  const priceValidUntilStr = priceValidUntil.toISOString().split('T')[0];

  // Helper function to create product schema with full details
  const createProductSchema = (product: typeof bondexProduct, productUrl: string, productImage: string) => {
    if (!product) return null;

    const productSchema: any = {
      '@type': 'Product',
      '@id': `${productUrl}#product`,
      name: product.name,
      image: [toAbsoluteUrl(productImage)],
      description: product.shortDescription || product.description,
      brand: { '@type': 'Brand', name: 'Bondex' },
      url: productUrl,
      sku: product.sku,
      mpn: product.mpn,
    };

    // Add Offer if price exists
    if (product.priceTRY) {
      const offer: any = {
        '@type': 'Offer',
        url: productUrl,
        priceCurrency: 'TRY',
        price: String(product.priceTRY),
        validFrom: validFromStr,
        priceValidUntil: priceValidUntilStr,
        availability: 'https://schema.org/InStock',
        itemCondition: 'https://schema.org/NewCondition',
        seller: { '@id': `${url}#organization` },
        hasMerchantReturnPolicy: { '@id': `${url}#merchantReturnPolicy` },
        shippingDetails: { '@id': `${url}#shippingDetails` },
      };

      // Add unitText if priceUnit is specified
      if (product.priceUnit === 'm2') {
        offer.unitText = 'm²';
        offer.priceSpecification = {
          '@type': 'UnitPriceSpecification',
          price: String(product.priceTRY),
          priceCurrency: 'TRY',
          unitCode: 'MTK', // Square meter
          unitText: 'm²',
        };
      }

      productSchema.offers = offer;
    }

    // Add AggregateRating if reviews exist
    if (product.reviews && product.reviews.length > 0) {
      const reviewCount = product.reviews.length;
      const ratingValue =
        product.reviews.reduce((sum, r) => sum + (Number.isFinite(r.ratingValue) ? r.ratingValue : 0), 0) / reviewCount;

      productSchema.aggregateRating = {
        '@type': 'AggregateRating',
        ratingValue: ratingValue.toFixed(1),
        reviewCount,
        bestRating: '5',
        worstRating: '1',
      };
    }

    return productSchema;
  };

  const schemaGraph: any[] = [
    {
      '@type': 'Organization',
      '@id': `${url}#organization`,
      name: 'bariyerlibondexsunger.com',
      url,
      logo: {
        '@type': 'ImageObject',
        url: toAbsoluteUrl('/images/bondex-logo.webp'),
        width: 512,
        height: 512,
      },
      contactPoint: [
        {
          '@type': 'ContactPoint',
          telephone: '+90 216 519 34 11',
          contactType: 'customer service',
          areaServed: 'TR',
          availableLanguage: ['tr'],
        },
        {
          '@type': 'ContactPoint',
          telephone: '+90 546 919 11 34',
          contactType: 'sales',
          areaServed: 'TR',
          availableLanguage: ['tr'],
        },
      ],
      sameAs: [
        'https://www.facebook.com/profile.php?id=61575206163572',
        'https://x.com/KabinPods',
        'https://www.instagram.com/kabinpods/',
        'https://www.youtube.com/channel/UCKk3RjOabrJb8JQYK9BjWkw',
        'https://www.linkedin.com/company/tasar%C4%B1m-akustik-mimarl%C4%B1k',
      ],
      // OpeningHoursSpecification for 2025 SEO
      openingHoursSpecification: [
        {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
          opens: '09:00',
          closes: '18:00',
        },
      ],
    },
    {
      '@type': 'WebSite',
      '@id': `${url}#website`,
      url,
      name: 'Bariyerli Bondex Sünger',
      description,
      publisher: { '@id': `${url}#organization` },
      inLanguage: 'tr-TR',
      potentialAction: {
        '@type': 'SearchAction',
        target: {
          '@type': 'EntryPoint',
          urlTemplate: `${url}urunler?q={search_term_string}`,
        },
        'query-input': 'required name=search_term_string',
      },
    },
    {
      '@type': 'WebPage',
      '@id': `${url}#webpage`,
      url,
      name: title,
      description,
      isPartOf: { '@id': `${url}#website` },
      inLanguage: 'tr-TR',
      datePublished: '2025-12-17',
      dateModified: new Date().toISOString().split('T')[0],
      primaryImageOfPage: {
        '@type': 'ImageObject',
        url: toAbsoluteUrl('/images/header-gorsel.webp'),
        width: 1600,
        height: 900,
      },
      breadcrumb: { '@id': `${url}#breadcrumb` },
      about: [
        { '@type': 'Thing', name: 'Bariyerli Bondex Sünger' },
        { '@type': 'Thing', name: 'Bondex Sünger' },
        { '@type': 'Thing', name: 'Çift Bariyerli Bondex Sünger' },
        { '@type': 'Thing', name: 'ses yalıtım süngeri' },
        { '@type': 'Thing', name: 'akustik sünger' },
      ],
    },
    {
      '@type': 'BreadcrumbList',
      '@id': `${url}#breadcrumb`,
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Anasayfa',
          item: url,
        },
      ],
    },
    // MerchantReturnPolicy schema (shared across all products)
    {
      '@type': 'MerchantReturnPolicy',
      '@id': `${url}#merchantReturnPolicy`,
      applicableCountry: {
        '@type': 'Country',
        name: 'TR',
      },
      returnPolicyCategory: 'https://schema.org/MerchantReturnFiniteReturnWindow',
      merchantReturnDays: 14,
      returnMethod: 'https://schema.org/ReturnByMail',
      returnFees: 'https://schema.org/FreeReturn',
    },
    // OfferShippingDetails schema (shared across all products)
    {
      '@type': 'OfferShippingDetails',
      '@id': `${url}#shippingDetails`,
      shippingRate: {
        '@type': 'MonetaryAmount',
        value: '0',
        currency: 'TRY',
      },
      shippingDestination: {
        '@type': 'DefinedRegion',
        addressCountry: {
          '@type': 'Country',
          name: 'TR',
        },
      },
      deliveryTime: {
        '@type': 'ShippingDeliveryTime',
        handlingTime: {
          '@type': 'QuantitativeValue',
          minValue: 1,
          maxValue: 3,
          unitCode: 'DAY',
        },
        transitTime: {
          '@type': 'QuantitativeValue',
          minValue: 1,
          maxValue: 5,
          unitCode: 'DAY',
        },
        businessDays: {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
        },
        cutoffTime: '14:00',
      },
    },
    (() => {
      const itemListElements = [
        {
          '@type': 'ListItem',
          position: 1,
          item: createProductSchema(
            bariyerliProduct,
            toAbsoluteUrl('/bariyerli-bondex-sunger'),
            '/images/Bariyerli-bondex.webp'
          ) || {
            '@type': 'Product',
            '@id': `${toAbsoluteUrl('/bariyerli-bondex-sunger')}#product`,
            name: 'Bariyerli Bondex Sünger',
            image: [toAbsoluteUrl('/images/Bariyerli-bondex.webp')],
            description: 'Bariyer katmanlı Bondex sünger ile daha yüksek ses yalıtım performansı.',
            brand: { '@type': 'Brand', name: 'Bondex' },
            url: toAbsoluteUrl('/bariyerli-bondex-sunger'),
            sku: 'BBDX-BASE',
            mpn: 'BBDX-BASE',
            offers: {
              '@type': 'Offer',
              url: toAbsoluteUrl('/bariyerli-bondex-sunger'),
              priceCurrency: 'TRY',
              price: '200',
              validFrom: validFromStr,
              priceValidUntil: priceValidUntilStr,
              availability: 'https://schema.org/InStock',
              itemCondition: 'https://schema.org/NewCondition',
              seller: { '@id': `${url}#organization` },
              hasMerchantReturnPolicy: { '@id': `${url}#merchantReturnPolicy` },
              shippingDetails: { '@id': `${url}#shippingDetails` },
              unitText: 'm²',
              priceSpecification: {
                '@type': 'UnitPriceSpecification',
                price: '200',
                priceCurrency: 'TRY',
                unitCode: 'MTK',
                unitText: 'm²',
              },
            },
          },
        },
        {
          '@type': 'ListItem',
          position: 2,
          item: createProductSchema(
            bondexProduct,
            toAbsoluteUrl('/bondex-sunger'),
            '/images/bondex-sunger.webp'
          ) || {
            '@type': 'Product',
            '@id': `${toAbsoluteUrl('/bondex-sunger')}#product`,
            name: 'Bondex Sünger',
            image: [toAbsoluteUrl('/images/bondex-sunger.webp')],
            description:
              'Yüksek yoğunluklu rebonded süngerpan (süngerpan) – ses yalıtımı ve titreşim sönümleme çözümleri.',
            brand: { '@type': 'Brand', name: 'Bondex' },
            url: toAbsoluteUrl('/bondex-sunger'),
            sku: 'BDX-BASE',
            mpn: 'BDX-BASE',
            offers: {
              '@type': 'Offer',
              url: toAbsoluteUrl('/bondex-sunger'),
              priceCurrency: 'TRY',
              price: '130',
              validFrom: validFromStr,
              priceValidUntil: priceValidUntilStr,
              availability: 'https://schema.org/InStock',
              itemCondition: 'https://schema.org/NewCondition',
              seller: { '@id': `${url}#organization` },
              hasMerchantReturnPolicy: { '@id': `${url}#merchantReturnPolicy` },
              shippingDetails: { '@id': `${url}#shippingDetails` },
              unitText: 'm²',
              priceSpecification: {
                '@type': 'UnitPriceSpecification',
                price: '130',
                priceCurrency: 'TRY',
                unitCode: 'MTK',
                unitText: 'm²',
              },
            },
          },
        },
        {
          '@type': 'ListItem',
          position: 3,
          item: createProductSchema(
            ciftBariyerliProduct,
            toAbsoluteUrl('/cift-bariyerli-bondex'),
            '/images/cift-bariyerli-bondex-26mm.webp'
          ) || {
            '@type': 'Product',
            '@id': `${toAbsoluteUrl('/cift-bariyerli-bondex')}#product`,
            name: 'Çift Bariyerli Bondex',
            image: [toAbsoluteUrl('/images/cift-bariyerli-bondex-26mm.webp')],
            description: 'Çift bariyer katmanı ile premium ses yalıtım çözümü.',
            brand: { '@type': 'Brand', name: 'Bondex' },
            url: toAbsoluteUrl('/cift-bariyerli-bondex'),
            sku: 'CBDX-BASE',
            mpn: 'CBDX-BASE',
            offers: {
              '@type': 'Offer',
              url: toAbsoluteUrl('/cift-bariyerli-bondex'),
              priceCurrency: 'TRY',
              price: '350',
              validFrom: validFromStr,
              priceValidUntil: priceValidUntilStr,
              availability: 'https://schema.org/InStock',
              itemCondition: 'https://schema.org/NewCondition',
              seller: { '@id': `${url}#organization` },
              hasMerchantReturnPolicy: { '@id': `${url}#merchantReturnPolicy` },
              shippingDetails: { '@id': `${url}#shippingDetails` },
              unitText: 'm²',
              priceSpecification: {
                '@type': 'UnitPriceSpecification',
                price: '350',
                priceCurrency: 'TRY',
                unitCode: 'MTK',
                unitText: 'm²',
              },
            },
          },
        },
      ].filter((item) => item.item !== null);

      return {
        '@type': 'ItemList',
        numberOfItems: itemListElements.length,
        itemListElement: itemListElements,
      };
    })(),
    {
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'Bariyerli Bondex Sünger nedir?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Bariyerli Bondex Sünger; Bondex Sünger ile ses bariyeri katmanının birleştiği, hava kaynaklı ses geçişini azaltmaya odaklı kompozit bir ses yalıtım çözümüdür. Bariyer katmanı sayesinde klasik Bondex Sünger\'e göre daha yüksek izolasyon performansı sağlar. Duvar, kapı ve yüksek izolasyon gereken projelerde tercih edilir. Bariyerli Bondex Sünger fiyatları kalınlık, metraj ve proje adedine göre değişir.',
          },
        },
        {
          '@type': 'Question',
          name: 'Bariyerli Bondex Sünger ile Bondex Sünger arasındaki fark nedir?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Bondex Sünger; rebonded süngerpan yapısıyla ses yalıtımı ve titreşim sönümleme için kullanılan yüksek yoğunluklu bir sünger türüdür. Bariyerli Bondex Sünger ise Bondex Sünger\'e ek olarak ses bariyeri katmanı içeren kompozit bir çözümdür. Bariyer katmanı sayesinde hava kaynaklı ses geçişini daha etkili şekilde azaltır. Daha yüksek izolasyon hedeflerinde Bariyerli Bondex Sünger tercih edilir.',
          },
        },
        {
          '@type': 'Question',
          name: 'Bariyerli Bondex Sünger mi, Çift Bariyerli Bondex Sünger mi tercih edilmeli?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Bariyerli Bondex Sünger; tek bariyer katmanı ile yüksek izolasyon gereken projeler için idealdir. Çift Bariyerli Bondex Sünger ise çift bariyer katmanı ile maksimum izolasyon performansı sunar. Proje ihtiyacınıza göre: standart yüksek izolasyon için Bariyerli Bondex Sünger, maksimum izolasyon gereken kritik projeler için Çift Bariyerli Bondex Sünger tercih edilir. Doğru seçim için proje detaylarınızı paylaşın, size en uygun çözümü önerelim.',
          },
        },
        {
          '@type': 'Question',
          name: 'Bariyerli Bondex Sünger fiyatları ne kadar?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Bariyerli Bondex Sünger fiyatları metrekare (m²) başına 200-310 TL arası değişir. Kalınlığa göre: 23mm 200-230 TL/m², 33mm 240-270 TL/m², 43mm 280-310 TL/m², 53mm 300-330 TL/m². Bondex Sünger 130-260 TL/m², Çift Bariyerli Bondex Sünger 350-450 TL/m² arası. Fiyatlar metraj ve proje adedine göre değişir. Bariyerli Bondex Sünger için hızlı teklif almak için WhatsApp üzerinden iletişime geçebilirsiniz.',
          },
        },
        {
          '@type': 'Question',
          name: 'Bariyerli Bondex Sünger nasıl uygulanır?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Bariyerli Bondex Sünger uygulaması yüzeye ve proje detayına göre değişir. Çoğu senaryoda uygun yapıştırıcı ile duvar, kapı veya zemin yüzeyine sabitlenir. Bariyer katmanının doğru yönde yerleştirilmesi ve kesim/ek yerlerinin düzgün yapılması performansı etkiler. Proje bazlı uygulama önerilir. Uzman ekibimiz size Bariyerli Bondex Sünger uygulama desteği sağlayabilir.',
          },
        },
        {
          '@type': 'Question',
          name: 'Bariyerli Bondex Sünger hangi projelerde kullanılır?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Bariyerli Bondex Sünger; stüdyo ve kayıt odası ses yalıtımı, ofis ve yaşam alanları izolasyonu, duvar ve kapı ses yalıtımı, yüksek izolasyon gereken endüstriyel projeler ve proje bazlı özel çözümlerde kullanılır. Bariyer katmanı sayesinde hava kaynaklı ses geçişini etkili şekilde azaltır. Türkiye geneline hızlı teslimat ile projelerinizde aksamaya yer vermiyoruz.',
          },
        },
      ],
    },
  ];

  const schema = {
    '@context': 'https://schema.org',
    '@graph': schemaGraph,
  };

  return (
    <JsonLd data={schema} />
  );
}
